import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCurrentUser } from '@/hooks/api/useAccount';
import {
  useBitbucketImportedUsers,
  useBitbucketStatus,
} from '@/hooks/api/useBitbucket';
import { useGithubImportedUsers, useGithubStatus } from '@/hooks/api/useGithub';
import { useMembers } from '@/hooks/api/useMembers';
import { useSecurityGroupByUser } from '@/hooks/api/useSecurityGroups';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useIsSupport } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { Plus, RefreshCw } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AddMemberModal } from '../components/AddMemberModal';
import { MembersTable } from '../components/MembersTable';

// TODO: Defer Platforms column implementation for now
// This will be added in a future phase when backend supports platform tracking

/**
 * Main Members tab component
 */
export default function MembersTab(): ReactElement | null {
  const [showAddModal, setShowAddModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // KIR-3908: filter the list to Discovered/Unattributed users only.
  const [showOnlyDiscovered, setShowOnlyDiscovered] = useState(false);
  const { organizationId } = useParams<{ organizationId: string }>();

  // Check if user is support
  const { isSupport } = useIsSupport(organizationId);

  // API hooks for members
  const {
    data: membersData,
    isLoading: membersLoading,
    error: membersError,
    refetch: refetchMembers,
  } = useMembers(organizationId);
  const members = useMemo(() => membersData ?? [], [membersData]);
  // React Query keeps the last successful `data` even when a later background
  // refetch (the 30s collaborative cadence / refetch-on-focus) errors, so a
  // loaded list stays trustworthy across transient failures. `data === undefined`
  // is the only state with no trustworthy list: the initial load is still
  // pending, or it failed before ever returning data (KIR-4040).
  const hasLoadedMembers = membersData !== undefined;
  const { data: currentUser } = useCurrentUser();
  const currentUserId = currentUser?.id || '';

  // Check if integration feature flags are enabled
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.GITHUB_INTEGRATION,
    flagsmithFeatureFlags.BITBUCKET_INTEGRATION,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  // KIR-3800 (AC #11). The members API carries no group field, so the column is
  // fed by a derived map; gated so no group request fires when the flag is off.
  const isSecurityGroupsEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;
  const { groupByUserId } = useSecurityGroupByUser(
    organizationId,
    isSecurityGroupsEnabled
  );
  const securityGroupNameByUserId = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(groupByUserId).map(([userId, group]) => [
          userId,
          group.name,
        ])
      ),
    [groupByUserId]
  );
  const isGithubIntegrationEnabled =
    import.meta.env.VITE_FF_GITHUB_INTEGRATION === 'true' ||
    !!flags[flagsmithFeatureFlags.GITHUB_INTEGRATION];
  const isBitbucketIntegrationEnabled =
    import.meta.env.VITE_FF_BITBUCKET_INTEGRATION === 'true' ||
    !!flags[flagsmithFeatureFlags.BITBUCKET_INTEGRATION];

  // Check if GitHub is connected to fetch imported users
  const { data: githubStatus } = useGithubStatus(organizationId ?? '');
  const isGithubConnected = githubStatus?.connected === true;

  // Check if Bitbucket is connected to fetch imported users
  const { data: bitbucketStatus } = useBitbucketStatus(organizationId ?? '');
  const isBitbucketConnected = bitbucketStatus?.connected === true;

  // Fetch GitHub imported users
  const {
    data: githubImportedResult,
    isLoading: githubImportedLoading,
    refetch: refetchGithubImported,
  } = useGithubImportedUsers(organizationId ?? '', {
    enabled: isGithubConnected,
  });

  // Fetch Bitbucket imported users
  const {
    data: bitbucketImportedResult,
    isLoading: bitbucketImportedLoading,
    refetch: refetchBitbucketImported,
  } = useBitbucketImportedUsers(organizationId ?? '', {
    enabled: isBitbucketConnected,
  });

  // Merge imported users from all providers
  const importedUsers = useMemo(() => {
    const github =
      isGithubConnected &&
      githubImportedResult &&
      'users' in githubImportedResult
        ? githubImportedResult.users
        : [];
    const bitbucket =
      isBitbucketConnected &&
      bitbucketImportedResult &&
      'users' in bitbucketImportedResult
        ? bitbucketImportedResult.users
        : [];
    return [...github, ...bitbucket];
  }, [
    isGithubConnected,
    githubImportedResult,
    isBitbucketConnected,
    bitbucketImportedResult,
  ]);

  // Lower-cased emails already in the org (real members + pending invites) so
  // the Add Member modal can block re-adding an existing user (KIR-4040).
  // KIR-3908: exclude Discovered/Unattributed rows — they are NOT accounts, and
  // Add Member is a valid path to convert a Discovered user (accept-time merge
  // folds its history), so blocklisting their email would wrongly reject it.
  const existingEmails = useMemo(
    () =>
      members
        .filter(m => !m.userType || m.userType === 'full')
        .map(m => m.email)
        .filter((e): e is string => Boolean(e))
        .map(e => e.toLowerCase()),
    [members]
  );

  if (!organizationId) {
    return null;
  }

  const isLoading =
    membersLoading ||
    (isGithubConnected && githubImportedLoading) ||
    (isBitbucketConnected && bitbucketImportedLoading);

  const hasAnyIntegration =
    isGithubIntegrationEnabled || isBitbucketIntegrationEnabled;

  // KIR-3908: Discovered/Unattributed users always need the Status + Source
  // columns (to show their kind + "Cowork"), even without an SCM integration.
  const hasDiscoveredUsers = members.some(
    m => m.userType && m.userType !== 'full'
  );
  const showStatusAndSourceColumns = hasAnyIntegration || hasDiscoveredUsers;
  const visibleMembers = showOnlyDiscovered
    ? members.filter(m => m.userType && m.userType !== 'full')
    : members;

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchMembers(),
        isGithubConnected ? refetchGithubImported() : Promise.resolve(),
        isBitbucketConnected ? refetchBitbucketImported() : Promise.resolve(),
      ]);
    } finally {
      // Add a small delay so the spinner is visible
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-end gap-2 mb-4">
          {hasDiscoveredUsers && (
            <Button
              variant={showOnlyDiscovered ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowOnlyDiscovered(v => !v)}
            >
              Discovered only
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          {/*
            Also gate on the members query state (KIR-4040): the Add Member
            modal's duplicate guard is fed `existingEmails` derived from the
            loaded members list. Disable until a list has actually loaded
            (`hasLoadedMembers`) so the modal never opens with an incomplete
            blocklist. We intentionally do NOT disable on `membersError` alone —
            React Query keeps the cached list when a background refetch errors,
            so `existingEmails` stays trustworthy and the button should remain
            usable; only a never-loaded list (initial load pending or failed)
            blocks it.
          */}
          <Button
            onClick={() => setShowAddModal(true)}
            disabled={isSupport || !hasLoadedMembers}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Member
          </Button>
        </div>
        {membersError && (
          <div className="text-red-600 text-sm mb-4">
            Failed to load members. Please try again.
          </div>
        )}
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading members...
          </div>
        ) : (
          <MembersTable
            members={visibleMembers}
            importedUsers={showOnlyDiscovered ? [] : importedUsers}
            currentUserId={currentUserId}
            organizationId={organizationId}
            showStatusAndSourceColumns={showStatusAndSourceColumns}
            showSecurityGroupColumn={isSecurityGroupsEnabled}
            securityGroupNameByUserId={securityGroupNameByUserId}
          />
        )}
      </CardContent>

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        organizationId={organizationId}
        existingEmails={existingEmails}
      />
    </Card>
  );
}
